// react-i18next configuration.
//
// Policy (WEBSITE-PLAN §6):
//   * zh is the DEFAULT and the SOURCE OF TRUTH; en is a real translation.
//   * The active language is driven by the URL `:lang` param (App.tsx calls
//     i18n.changeLanguage(lang) + sets <html lang>). This module just initializes.
//   * NO auto-fallback to zh. Falling back would silently hide English gaps
//     (a half-Chinese English page reads as a bug, not a graceful default), so
//     `fallbackLng` is disabled and a dev-only missing-key logger surfaces gaps.
//
// Namespaces are split per page (lazy-registered). Agent C (this agent) owns the
// `common` + `contact` bundles; Agent D fills home/about/services/network/partners
// under the same key structure (web/src/i18n/{zh,en}/<ns>.json).
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import zhCommon from "./zh/common.json";
import zhHome from "./zh/home.json";
import zhAbout from "./zh/about.json";
import zhServices from "./zh/services.json";
import zhNetwork from "./zh/network.json";
import zhPartners from "./zh/partners.json";
import zhContact from "./zh/contact.json";

import enCommon from "./en/common.json";
import enHome from "./en/home.json";
import enAbout from "./en/about.json";
import enServices from "./en/services.json";
import enNetwork from "./en/network.json";
import enPartners from "./en/partners.json";
import enContact from "./en/contact.json";

export const SUPPORTED_LOCALES = ["zh", "en"] as const;
export type AppLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: AppLocale = "zh";

/** Namespaces registered with i18next. Page namespaces are filled by Agent D. */
export const NAMESPACES = [
  "common",
  "home",
  "about",
  "services",
  "network",
  "partners",
  "contact",
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export const resources = {
  zh: {
    common: zhCommon,
    home: zhHome,
    about: zhAbout,
    services: zhServices,
    network: zhNetwork,
    partners: zhPartners,
    contact: zhContact,
  },
  en: {
    common: enCommon,
    home: enHome,
    about: enAbout,
    services: enServices,
    network: enNetwork,
    partners: enPartners,
    contact: enContact,
  },
} as const;

// Vite injects `import.meta.env.DEV`. The scaffold has no `vite/client` types
// referenced, so read it through a narrow local cast to stay type-safe without
// touching shared config files.
const importMetaEnv = (import.meta as unknown as { env?: { DEV?: boolean } }).env;
const isDev = importMetaEnv?.DEV ?? false;

void i18n.use(initReactI18next).init({
  resources,
  lng: DEFAULT_LOCALE,
  // No auto-fallback: we want missing keys to be VISIBLE (logged in dev), not
  // silently masked by the other locale. App.tsx sets the active language.
  fallbackLng: false,
  supportedLngs: [...SUPPORTED_LOCALES],
  defaultNS: "common",
  ns: [...NAMESPACES],
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
  returnEmptyString: false,
  // Dev-only: log any key we look up but can't resolve, so en/zh gaps surface.
  saveMissing: isDev,
  missingKeyHandler: isDev
    ? (lngs, ns, key) => {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] MISSING ${lngs.join(",")} :: ${ns}:${key}`);
      }
    : undefined,
});

/** Map an app locale to the value used in <html lang>. */
export function htmlLang(locale: AppLocale): string {
  return locale === "zh" ? "zh-Hans" : "en";
}

export default i18n;
